package org.multiplatform.kotfolio.pages.compose.internal

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.components.WebImage
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import org.multiplatform.kotfolio.util.Resources
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Ul

@Page("/compose/internal/render_process")
@Composable
fun InternalRenderProcessComposePage() {
    BlogLayout(
        PageProperties(
            "Compose internal render process",
            "Explain the compose internal render process and internal Render Process (Composition Phases)."
        ),
        BlogProperties("Jetpack Compose", "Internal Render Process (Composition Phases)")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            SpanText(
                text = "Main phases of a frame",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A frame is a single complete image displayed on your screen, like one photograph in a flip-book animation. Your phone’s display shows 60 of these images per second (on 60Hz displays), with each frame appearing every 16.67 milliseconds. If your app can’t provide a new frame in time, users see stuttering or jank.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Every frame goes through a strict pipeline: Composition (build UI tree), Layout (measure and position elements), Drawing (create drawing commands), and GPU Rendering (convert to pixels). Side effects execute at specific points in this pipeline, determining what users actually see and when.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Compose's architecture is based on 3 layers of collaboration",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compose Compiler → converts code",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compose Runtime → manages status and recomposition",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compose Ul → provides interface elements",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Render process of compose / Main phases Composition / Compose phases",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Jetpack Compose follows a well-defined rendering pipeline divided into three key phases: Composition, Layout, and Drawing. These phases work sequentially to build, arrange, and render the UI on the screen efficiently",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                WebImage(
                    src = Resources.Images.RENDER_COMPOSE_2,
                    modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                    contentDescription = "Render process"
                )

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The View hierarchy is like a tree you know, the parent ViewGroup starts at the root and traverses down to the leaf Views. Each phase trickles down the hierarchy..",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Measure and Layout phases always happen together, sequentially, before drawing.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This is a unidirectional flow that happens from composition to drawing to produce a frame.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Each frame that is drawn on the screen goes through different phases. Compose renders its composables through three steps:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                WebImage(
                    src = Resources.Images.RENDER_COMPOSE_1,
                    modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                    contentDescription = "Render process"
                )

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Composition - (Define the UI structure using composables - What Ul to show)",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .fontWeight(FontWeight.Bold)
                        )
                    }

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Composition runs your @Composable functions and builds/mutates the node tree via the Applier.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Compose executes your composable functions and builds a UI tree, describing what should be on screen. (produce/update the tree of layout nodes by running @Composable functions)",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = " During this phase, Compose builds the initial UI structure and records the relationships between composables in a data structure called the Slot Table.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "During the composition phase, the Compose runtime executes your composable functions.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "It outputs a tree data structure that represents your UI. This UI tree consists of layout nodes.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Together, these layout nodes hold all the information needed to complete the next phases.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "hen state changes occur, the composition phase recalculates affected parts of the UI and triggers recomposition where necessary. Compose optimizes this step by skipping recomposition when possible.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Ul {
                            Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "Key Tasks in Composition",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                        .fontWeight(FontWeight.Bold)
                                )
                            }

                            Ul {
                                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                                    SpanText(
                                        text = "Executing @Composable functions",
                                        modifier = Modifier
                                            .fontSize(FontSize.Medium)
                                    )
                                }

                                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                    SpanText(
                                        text = "Creating and updating the UI tree",
                                        modifier = Modifier
                                            .fontSize(FontSize.Medium)
                                    )
                                }

                                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                    SpanText(
                                        text = "Tracking changes for recomposition",
                                        modifier = Modifier
                                            .fontSize(FontSize.Medium)
                                    )
                                }
                            }
                        }
                    }

                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Recomposition",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .fontWeight(FontWeight.Bold)
                        )
                    }

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "When a state changes, Compose intelligently re-executes only the affected functions instead of redrawing everything — thanks to its slot table and key-based recomposition tracking.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }

                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Measure - How big do you want to be?",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .fontWeight(FontWeight.Bold)
                        )
                    }

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "onMeasure() decides how much space the View needs, based on:",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "The parent Composable tells its children how much space they can occupy.",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }
                        }

                        Ul {
                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "Parent constraints (MeasureSpec)",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "Your content",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "Padding",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "desired intrinsic size",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }
                        }

                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "It does NOT position anything and must not draw.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }

                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Layout - (Determine the size and position and position of each element within the UI tree - Where to place Ul)",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .fontWeight(FontWeight.Bold)
                        )
                    }

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Layout measures every node and places it in 2D space.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Children decide their own size and position within the parent.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "It determines the size and position of each UI element based on provided constraints. Each composable measures its children, decides its dimensions, and defines its position relative to its parent. Measure each node's size and position children within parents.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Where to place UI? This phase consists of two steps: measurement and placement. Compose measures the children if any, and place themselves accordingly in the 2D space.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Then, during the layout phase, each element in the tree measures its children, if any, and places them in the available 2D space.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "We position each Composable within the UI Tree, and for this, we need to measure each node so that it can be precisely placed in the UI.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "As in the image above, Layout will calculate the size of the position of each UI node in 2D coordinates. For example: the Image will have a size 200x200 and is placed in the position (0,0) in its parent Node (Row). The layout-phare is a bottom-up approach, it means child nodes will calculate their size, and position first and report it to the parent nodes.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "This phase consists of two steps: 𝗠𝗲𝗮𝘀𝘂𝗿𝗲𝗺𝗲𝗻𝘁𝘀 : Each element calculates its size. 2. 𝗣𝗹𝗮𝗰𝗲𝗺𝗲𝗻𝘁 : Elements are positioned within their parent.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Ul {
                            Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "Key Tasks in Drawing",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                        .fontWeight(FontWeight.Bold)
                                )
                            }

                            Ul {
                                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                                    SpanText(
                                        text = "Measuring UI components",
                                        modifier = Modifier
                                            .fontSize(FontSize.Medium)
                                    )
                                }

                                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                    SpanText(
                                        text = "Defining width, height, and positions",
                                        modifier = Modifier
                                            .fontSize(FontSize.Medium)
                                    )
                                }

                                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                    SpanText(
                                        text = "Arranging children within parent containers",
                                        modifier = Modifier
                                            .fontSize(FontSize.Medium)
                                    )
                                }
                            }
                        }
                    }

                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Drawing - (Render the final UI on the screen - How it renders)",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .fontWeight(FontWeight.Bold)
                        )
                    }

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "How it renders. Rendering it on pixels. Issue draw commands to the rendering backend (Android: Skia via the Compose renderer; iOS/Desktop: Skia via Skiko).",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Finally, in the drawing phase, each node in the tree draws its pixels on the screen",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "This vital step visually constructs your UI, making the designed composables available for user interaction.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "In this phase the tree is traversed again from top to bottom and each node draws itself in the screen in turn.  If only visual properties change (like color or text) , compose may skip the composition and layout phases, optimizing performance.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Just ask GPU to draw all UI nodes to the screen bottom-down (Draw parent nodes first and then its children).",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Ul {
                            Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "Key Tasks in Drawing",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                        .fontWeight(FontWeight.Bold)
                                )
                            }

                            Ul {
                                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                                    SpanText(
                                        text = "Rendering visual elements",
                                        modifier = Modifier
                                            .fontSize(FontSize.Medium)
                                    )
                                }

                                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                    SpanText(
                                        text = "Drawing UI components onto the screen",
                                        modifier = Modifier
                                            .fontSize(FontSize.Medium)
                                    )
                                }

                                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                    SpanText(
                                        text = "Applying custom drawing operations",
                                        modifier = Modifier
                                            .fontSize(FontSize.Medium)
                                    )
                                }
                            }
                        }
                    }
                }
            }

            KotlinCode(
                code = """
                    @Composable
                    fun OffsetExample() {
                        val offset = remember { Animatable(0f) }
                        LaunchedEffect(Unit) { offset.animateTo(100f) }
                        
                        // [BAD] Reads `offset.value` during COMPOSITION
                            - Every animation frame triggers full recomposition
                            - 60 frames per second -> 60 recompositions per second
                        
                        Box(   
                            modifier = Modifier.offset(x = offset.value.dp, y = 0.dp)
                        )
                        
                        // [OK] Reads `offset.value` during LAYOUT only
                            - Composition runs once
                            - Each animation frame only re-runs layout
                        
                        Box(
                            modifier = Modifier.offset { IntOffset(offset.value.toInt(), 0) }
                        )
                        
                        // [BEST] For pure visual changes, GraphicsLayer reads in DRAWING only
                            - Composition runs once, layout runs once
                            - Each animation frame only re-runs drawing
                        
                        Box(
                            modifier = Modifier.graphicsLayer { translationX = offset.value }
                        )
                    }
                    """.trimIndent()
            )

            Ol {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Composition phase",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Direct calls to State<T>.value or `by` delegated state at the composable function level",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Modifier parameters: Modifier.padding(value.dp) <- read in composition",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Conditional composition: if (state.isLoading) { Loader() }",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Layout phase",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Lambda-based modifiers: Modifier.offset { IntOffset(x, y) }\n" +
                                    "   Modifier.layout { measurable, constraints -> ... }",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "LazyColumn item visibility decisions",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Drawing phase",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Modifier.drawBehind { drawRect(color) }",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Modifier.graphicsLayer { rotationZ = state.value }",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Custom drawing in Canvas composable",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Rule of thumb for animations",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Color, alpha, rotation, translation -> graphicsLayer (drawing)",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Position changes -> offset { } (layout)",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Anything that affects sibling layout -> must be composition",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "What is recomposition, and when does it occur? Also, how does it related to the app performance?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "To update a UI layout that has already been rendered through the three primary phases of Jetpack Compose (Composition, Layout, and Drawing), the framework introduces a mechanism for redrawing the UI when state changes occur. This process is known as Recomposition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When recomposition happens, Compose starts from the Composition phase, where composable nodes notify the framework about UI changes, ensuring the updated UI reflects the latest state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            WebImage(
                src = Resources.Images.RECOMPOSITION_PHASE_1,
                modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                contentDescription = "Recomposition"
            )

            SpanText(
                text = "Conditions That Trigger Recomposition",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Most mobile applications maintain state, which serves as an in-memory representation of the app’s data model. To ensure the UI stays in sync with state changes, Jetpack Compose triggers recomposition through two primary mechanisms:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Input Changes: Composable functions trigger recomposition when their input parameters change. The Compose runtime compares new arguments against previous ones using the equals() function. If the comparison returns false, the runtime detects a change and triggers recomposition, updating the relevant parts of the UI.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Observing State Changes: Jetpack Compose monitors state changes using its State API, typically in combination with the remember function. This approach preserves state objects in memory and restores them during recomposition, ensuring that the UI consistently reflects the latest state without manual intervention.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "Generic Node Types: One Runtime, Many Targets",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Compose Runtime is actually generic. It doesn’t care what the nodes represent; it only cares that they form a tree.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "LayoutNodes: Used in standard UI. They interact with the Android screen.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Vector Nodes: Used inside VectorPainter. When you define a Vector image in Compose, it builds a specialized internal node tree distinct from the UI tree.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Mosaic Nodes: Used for terminal-based UIs (rendering text to a console).",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Runtime treats them all the same: as a logical tree that needs to be updated based on state changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Is a LayoutNode the same as a Graphics Layer?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "No. A LayoutNode is a logical UI element. A Graphics Layer is a specialized hardware-accelerated surface used for effects like rotation, scaling, or alpha. One LayoutNode can have multiple layers, or none at all.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Why does Compose feel faster than the View system?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The View system often suffers from “Double Taxation” — measuring and laying out multiple times for a single change. LayoutNode enforces a single-pass measurement policy, making the layout phase significantly more predictable and efficient.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Can I build my own Applier?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Yes! This is how projects like Mosaic (for console UI) or Redwood work. You define your own Node types and an Applier that knows how to manage them, and you can use Compose’s state management for literally anything — even non-UI trees like document structures.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Compose transforms the state into UI",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                WebImage(
                    src = Resources.Images.TRANSFORMS_STATE_COMPOSE_1,
                    modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                    contentDescription = "Transforms state 1"
                )

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The View hierarchy is like a tree you know, the parent ViewGroup starts at the root and traverses down to the leaf Views. Each phase trickles down the hierarchy.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Measure and Layout phases always happen together, sequentially, before drawing.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Composition executes your composable functions which can emit UI, creating a UI tree. For example, executing this SearchResult composable yields a tree like this.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                WebImage(
                    src = Resources.Images.TRANSFORMS_STATE_COMPOSE_2,
                    modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                    contentDescription = "Transforms state 2"
                )

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In the layout stage, this tree is worked and each piece of UI is measured and placed on the screen. That is each node determines their width and height and x, y coordinates. Each element is also located within its parent, specified as an (x, y) position, and a size, specified as a width and a height. In the drawing stage, the UI tree is worked again, and all elements are rendered.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Layout stage",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            WebImage(
                src = Resources.Images.LAYOUT_STAGE_COMPOSE_1,
                modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                contentDescription = "Layout stage"
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When we move to the layout phase, we use this UI tree as input. The collection of layout nodes contain all the information needed to eventually decide on each node’s size and location in 2D space.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "During the Layout phase, the tree is traversed using the following 3 step algorithm:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Measure children: A node measures its children, if any.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Decide own size: Based on those measurements, a node decides on its own size.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Place children: Each child node is placed relative to a node’s own position.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The layout phase consists of two steps: measurement and placement.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "A parent hands its children Constraints (min/max width and height); each child measures itself and reports back a Placeable. The measurement step runs the measure lambda passed to the Layout composable, the MeasureScope.measure method of the LayoutModifier interface, and so on.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The parent decides the (x, y) for each Placeable and positions it. The placement step runs the placement block of the layout function, the lambda block of Modifier.offset { … }, and so on.",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State reads during each of these steps affect the layout and potentially the drawing phase. When the state value changes, Compose UI schedules the layout phase. It also runs the drawing phase if size or position has changed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "To be more precise, the measurement step and the placement step have separate restart scopes, meaning that state reads in the placement step don’t re-invoke the measurement step before that. However, these two steps are often intertwined, so a state read in the placement step can affect other restart scopes that belong to the measurement step.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                WebImage(
                    src = Resources.Images.LAYOUT_STAGE_COMPOSE_2,
                    modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                    contentDescription = "Layout stage"
                )

                WebImage(
                    src = Resources.Images.LAYOUT_STAGE_COMPOSE_3,
                    modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                    contentDescription = "Layout stage"
                )

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "At the end of the phase, each layout node will have an assigned width and height, and an x, y coordinate where it should be drawn.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "So for our composable, this would work as follows:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The Row measures its children.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "First, the Image is measured. It doesn’t have any children so it decides its own size and reports it back to the Row.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Second, the Column is measured. It needs to measure its own children first.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The first Text is measured. It doesn’t have any children so it decides its own size and reports it back to the Column.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The second Text is measured. It doesn’t have any children so it decides its own size and reports it back to the Column.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The Column uses the child measurements to decide its own size. It uses the maximum child width and the sum of the height of its children.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The Column places its children relative to itself, putting them beneath each other vertically.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The Row uses the child measurements to decide its own size. It uses the maximum child height and the sum of the widths of its children. It then places its children.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "One key take-away here is that we visited each node only once. With a single pass through the UI tree we were able to measure and place all the nodes. This is great for performance. When the number of nodes in the tree increases, the time spent traversing it increases in a linear fashion. In contrast, if we were to visit each node multiple times, the traversal time would increase exponentially.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            Ol {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "onMeasure(int widthMeasureSpec, int heightMeasureSpec)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Each View calculates how big it wants to be.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "For a ViewGroup, it will call onMeasure() on each of its children, then decide its own size based on their measurements.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "You don’t return values here. Instead, you call setMeasuredDimension(width, height) to store the measured size (when overriding onMeasure() in custom Views or ViewGroups).",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    KotlinCode(
                        code = """
                        override fun onMeasure(widthMeasureSpec: Int, heightMeasureSpec: Int) {
                            // Custom measurement logic here
                            setMeasuredDimension(calculatedWidth, calculatedHeight)
                        }
                    """.trimIndent()
                    )

                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Each MeasureSpec is a combination of a mode and a size, passed from parent to child. The modes are:",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Ol {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "EXACTLY — The parent has determined an exact size for the child.\n",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "AT_MOST — The child can be as big as it wants up to a maximum size.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "UNSPECIFIED — The parent doesn’t impose any constraint. The child can be whatever it wants.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                //2
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "onLayout(boolean changed, int left, int top, int right, int bottom)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Once measurement is done, it’s time to place Views on the screen. This method positions each View within its parent by setting their bounds.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Custom ViewGroups must override this to arrange their children manually. Think of this as arranging furniture in a room after measuring the space.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                //3
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "onDraw(Canvas canvas)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Finally, Views show themselves on screen. This method is where the actual drawing happens, using the Canvas object",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Important: Don’t allocate new objects here in onDraw(). onDraw() can run many times per second, and allocations here are basically open invitations for the Garbage Collector.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "State Changes and Redrawing",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ol {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "invalidate()",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Tells the system: “I’ve changed visually. Please redraw me.” It only triggers the Draw phase.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "requestLayout()",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "says: “My size or position changed. Let’s re-measure and lay out again.”, so it needs to go through Measure and Layout again (and eventually Draw too). Use this when you’ve changed something that affects positioning or size.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "Conditional Layout View System",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ol {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Inflate the layout",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "All views would be inflated regardless of space.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Measure in onMeasure",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "In a custom ViewGroup's onMeasure method, you could iteratively measure child views. After measuring a child, you'd check if getMeasuredHeight() of that child, plus the y offset so far, exceeded the heightMeasureSpec (available height).",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Conditional Visibility",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "If a child didn’t fit, you could then set its visibility to View.GONE and re-layout.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "Subcompositions from a Compose UI Perspective",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compositions don't just exist at the root; they can be created as children of other compositions and linked together. There are two main reasons for creating subcompositions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "SubcomposeLayout",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "SubcomposeLayout solves this by deferring the composition of the dependent child until the measurement of the primary child is finalized—making the logic atomic within a single UI frame.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "SubcomposeLayout allows composing parts of a layout on demand. This is especially useful for cases where some parts of the UI are resource-intensive or may not be immediately available, like loading an image from the network or a database.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The only reason to use Subcomposition is when you have conditional Composition (what components to show) based on a value that is not available until the measure & layout phase.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "E.g: available space in parent, or the size of children.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "subcompose(): This function is used to compose individual parts of the layout. The subcompose function returns a list of MeasureResult objects, which you can then measure and place on the screen.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Why use LayoutSlot enums instead of strings?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "While subcompose(\"Main\") works, using an enum or object is a best practice. It prevents typos and ensures the slot keys are stable, which helps the Compose compiler track the subcomposition state more efficiently.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "How does this differ from BoxWithConstraints?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "BoxWithConstraints is actually built on top of SubcomposeLayout. The difference is scope: BoxWithConstraints gives you the parent's constraints, while SubcomposeLayout gives you the power to measure a sibling first and use those results to decide what to compose next.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Does this handle multi-line wrapping?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The example above is optimized for a single row. If you want the dependentAction to wrap to a second line, you would need to adjust the layoutheight and placeRelative logic to account for a Y-offset.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    // Use stable keys to help the Compose compiler track slots across recompositions
                    private enum class LayoutSlot { Main, Dependent }

                    @Composable
                    fun AdaptiveActionRow(
                        mainContent: @Composable () -> Unit,
                        dependentAction: @Composable (availableWidth: Int) -> Unit,
                        modifier: Modifier = Modifier
                    ) {
                        SubcomposeLayout(modifier = modifier) { constraints ->
                            // 1. Subcompose and measure the Main Content first
                            // We use minWidth = 0 so the child isn't forced to fill the screen
                            val mainPlaceables = subcompose(LayoutSlot.Main, mainContent).map {
                                it.measure(constraints.copy(minWidth = 0))
                            }

                            val mainWidth = mainPlaceables.maxOfOrNull { it.width } ?: 0
                            
                            // 2. Safe Constraint Handling
                            // If the parent is in a scrollable Row (maxWidth is Infinity),
                            // we fallback to the mainWidth as our boundary to prevent logic errors.
                            val maxAvailable = if (constraints.hasBoundedWidth) constraints.maxWidth else mainWidth
                            val remainingWidth = (maxAvailable - mainWidth).coerceAtLeast(0)

                            // 3. Subcompose the dependent piece with the "Knowledge" of remaining space
                            val actionPlaceables = subcompose(LayoutSlot.Dependent) {
                                dependentAction(remainingWidth)
                            }.map {
                                it.measure(
                                    constraints.copy(
                                        minWidth = 0, 
                                        maxWidth = remainingWidth // Force the child to respect the gap
                                    )
                                )
                            }

                            val actionWidth = actionPlaceables.maxOfOrNull { it.width } ?: 0
                            val totalWidth = if (constraints.hasBoundedWidth) constraints.maxWidth else mainWidth + actionWidth
                            
                            val layoutHeight = maxOf(
                                mainPlaceables.maxOfOrNull { it.height } ?: 0,
                                actionPlaceables.maxOfOrNull { it.height } ?: 0
                            )

                            // 4. Final Placement Pass
                            layout(totalWidth, layoutHeight) {
                                mainPlaceables.forEach { it.placeRelative(0, 0) }
                                
                                // Align the action to the end of the calculated row
                                actionPlaceables.forEach { 
                                    it.placeRelative(totalWidth - actionWidth, 0) 
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun ImageWithPlaceholder(imageUrl: String, placeholder: Painter) {
                        Box(modifier = Modifier.fillMaxSize()) {
                            SubcomposeLayout { constraints ->
                                // First, compose the placeholder
                                val placeholderLayout = subcompose(0) {
                                    Image(painter = placeholder, contentDescription = null, modifier = Modifier.fillMaxSize())
                                }

                                // Compose the image once it's loaded
                                val imageLayout = subcompose(1) {
                                    AsyncImage(
                                        model = imageUrl,
                                        contentDescription = null,
                                        modifier = Modifier.fillMaxSize(),
                                        contentScale = ContentScale.Crop
                                    )
                                }

                                // Return the max size for the layout
                                layout(constraints.maxWidth, constraints.maxHeight) {
                                    placeholderLayout[0].measure(constraints).placeRelative(0, 0)
                                    imageLayout[0].measure(constraints).placeRelative(0, 0)
                                }
                            }
                        }
                    }

                    @Preview
                    @Composable
                    fun ImageWithPlaceholderPreview() {
                        ImageWithPlaceholder(
                            imageUrl = "https://www.example.com/image.jpg",
                            placeholder = painterResource(id = R.drawable.placeholder_image)
                        )
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "BoxWithConstraints",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "BoxWithConstraints achieves this through subcomposition—a mechanism that defers composition until measurement time.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @Composable
                    fun MessageDetailContent(
                        component: ChatDetailComponent,
                        modifier: Modifier = Modifier
                    ) {
                        val state by component.model.subscribeAsState()

                        BoxWithConstraints(modifier = modifier.fillMaxSize()) {
                            val isLargeScreen = maxWidth >= 840.dp
                            val contentMaxWidth = if (isLargeScreen) 600.dp else maxWidth

                            Scaffold(
                                modifier = Modifier
                                    .fillMaxSize()
                                    .then(
                                        if (isLargeScreen) Modifier.widthIn(max = contentMaxWidth)
                                            .align(Alignment.Center) else Modifier
                                    ),
                                topBar = { MessageAppBar(component, state.isBackButtonVisible) },
                                content = { paddingValues ->

                                },
                                bottomBar = {

                                },
                            )
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //LazyVerticalGrid using BoxWithConstraints

                    BoxWithConstraints {
                        val maxWidth = maxWidth
                        val columns = if (maxWidth > 800.dp) 5 else 2
                        val scrollState = rememberLazyGridState()

                        Column(
                            modifier = Modifier.fillMaxWidth()
                                .widthIn(max = if (columns == 5) 1080.dp else maxWidth),
                            horizontalAlignment = Alignment.CenterHorizontally,
                            verticalArrangement = Arrangement.Center
                        ) {
                            LazyVerticalGrid(
                                columns = GridCells.Fixed(columns),
                                state = scrollState,
                                verticalArrangement = Arrangement.spacedBy(8.dp),
                                horizontalArrangement = Arrangement.spacedBy(8.dp),
                                contentPadding = PaddingValues(16.dp)
                            ) {

                            }
                        }
                    }
                """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun AdaptiveCard() {
                        BoxWithConstraints {
                            if (maxWidth < 600.dp) {
                                Column {
                                    CardContent()
                                }
                            } else {
                                Row {
                                    CardContent()
                                }
                            }
                        }
                    }

                    @Composable
                    fun CardContent() {
                        Card {
                            Column {
                                Text("Title")
                                Spacer(modifier = Modifier.height(8.dp))
                                Text("Description")
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun BoxWithConstraints(
                        modifier: Modifier = Modifier,
                        contentAlignment: Alignment = Alignment.TopStart,
                        propagateMinConstraints: Boolean = false,
                        content: @Composable BoxWithConstraintsScope.() -> Unit,
                    ) {
                        SubcomposeLayout(modifier) { constraints ->
                            // We're NOW in the measurement phase, but we can compose!
                            val scope = BoxWithConstraintsScopeImpl(this, constraints)
                            val measurables = subcompose(Unit) { scope.content() }
                            
                            // Standard Box measurement logic follows...
                            with(measurePolicy) { measure(measurables, constraints) }
                        }
                    }
                    """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The key is SubcomposeLayout. It inverts the normal flow:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Normal: Composition → Measurement",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Subcompose: Measurement → Composition (of children) → Measurement (of children)",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When the parent asks BoxWithConstraints to measure itself, it:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Receives the constraints.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Creates a scope with those constraints.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Composes the children at that moment.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Measures the now-composed children.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Returns the measurement result.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This is why BoxWithConstraints is considered heavy—it performs composition work during the measurement phase.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Three-Tier Node Architecture",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Internally, SubcomposeLayout manages child nodes in three distinct regions:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Active nodes - (Currently used): Slots currently being used in this measure pass.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .fontWeight(FontWeight.Bold)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Reusable nodes - (Recycled for reuse): Previously active slots that are kept around (similar to a RecyclerView’s recycled views). When similar content is needed, these are “reactivated” instead of creating new compositions.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .fontWeight(FontWeight.Bold)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Precomposed nodes - (Pre-composed ahead of time): Slots composed ahead of time via precompose() (used internally by lazy lists to compose upcoming items before they're visible).",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .fontWeight(FontWeight.Bold)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This architecture is why LazyColumn scrolls smoothly—it's not creating and destroying compositions; it's shuffling nodes between these three pools.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }
            }

            SpanText(
                text = "Custom Layouts",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Jetpack Compose, a custom layout is governed by a MeasurePolicy.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This is a functional interface where the magic happens. When you call the Layout composable, you are implementing a single method: measure.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This method provides two vital inputs: measurables (the children) and constraints (the boundaries).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Your job is to transform those measurables into placeables — elements that have a fixed, concrete size.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }
            }

            SpanText(
                text = "Key Concepts of Measurements",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ol {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Layout(content, modifier, measurePolicy)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "This composable is your direct gateway to Compose's layout system.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "You provide a content lambda (your children) and a measurePolicy lambda where all the magic happens.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "measurables",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Inside measurePolicy, measurables is a list of all the children passed to your Layout composable.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Each Measurable represents a child that you can query and measure.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "constraints",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "This object is passed down from the parent of your Layout composable.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "It defines the minWidth, maxWidth, minHeight, and maxHeight that your Layout composable (and by extension, its children) must adhere to. Crucially, constraints.maxHeight tells us the total vertical space we have available.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "measurable.minIntrinsicHeight(width)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "This is the hero of our solution. Instead of giving us the measured height (which changes based on constraints), it tells us the minimum height this child would ideally take if given width and infinite height. It's a lightweight query that avoids a full layout pass. This is invaluable when working with unknown content sizes.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "measurable.measure(constraints.copy(...))",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "We then call measure() using a dynamically modified set of rules. The constraints.copy(maxHeight = constraints.maxHeight - yOffset) expression creates a new constraint on the fly, limiting the child’s height to only the vertical space that remains. This returns a Placeable—the measured child, now ready to be positioned.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "placeable.place(x, y)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "After all measurements are done, the layout lambda is called. Here, you iterate through your Placeable objects and tell Compose exactly where to draw them on the screen using their x and y coordinates.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            KotlinCode(
                code = """
                    @Composable
                    fun GenericConditionalLayout(
                        modifier: Modifier = Modifier,
                        content: @Composable () -> Unit
                    ) {
                        Layout(
                            content = content,
                            modifier = modifier
                        ) { measurables, constraints -> // 'constraints' here are from the parent

                            var yOffset = 0 // Tracks the current vertical position for placing children
                            val placedChildren = mutableListOf<Placeable>() // Stores children that will be placed

                            for (measurable in measurables) {
                                // Step 1: Query the child's natural height using minIntrinsicHeight
                                // This tells us the minimum height the child needs to display its content
                                // given the maximum width it can take.
                                val childIntrinsicHeight = measurable.minIntrinsicHeight(constraints.maxWidth)

                                // Step 2: Check if placing this child would exceed the available space
                                // constraints.maxHeight is the total height allowed by the parent.
                                if (yOffset + childIntrinsicHeight <= constraints.maxHeight) {
                                    // If it fits:
                                    // Perform a full measure pass for the child using the remaining space.
                                    val placeable = measurable.measure(
                                        constraints.copy(minHeight = 0, maxHeight = constraints.maxHeight - yOffset)
                                    )
                                    placedChildren.add(placeable)
                                    yOffset += placeable.height // Update offset for the next child
                                } else {
                                    // If it does NOT fit:
                                    // Stop the loop. No more children will be measured or placed,
                                    // ensuring no overflow.
                                    break
                                }
                            }

                            // Step 3: Determine the size of our custom layout and place the children
                            layout(constraints.maxWidth, constraints.maxHeight) {
                                var currentY = 0
                                placedChildren.forEach { placeable ->
                                    placeable.place(x = 0, y = currentY) // Place each child at its calculated Y position
                                    currentY += placeable.height // Update Y for the next child
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/xephosbot/AnilibriaRefresh
            KotlinCode(
                code = """
                    @Composable
                    private fun ReleaseListItemContent(
                        release: Release,
                        modifier: Modifier = Modifier,
                    ) {
                        val releaseTitle by releaseTitleState(release)

                        ListItemLayout(
                            modifier = modifier.height(ReleaseItemContainerHeight),
                            headlineContent = {
                                Text(
                                    text = release.localizedName(),
                                    overflow = TextOverflow.Ellipsis,
                                )
                            },
                            supportingContent = {
                                Text(
                                    text = release.description?.lines()?.joinToString(" ").orEmpty(),
                                    overflow = TextOverflow.Ellipsis,
                                )
                            },
                            leadingContent = {
                                PosterImage(release.poster)
                            },
                            tags = {
                                Text(
                                    text = releaseTitle,
                                    maxLines = 1,
                                    overflow = TextOverflow.Ellipsis
                                )
                            },
                        )
                    }
                    
                    @Composable
                    private fun ListItemLayout(
                        modifier: Modifier = Modifier,
                        headlineContent: @Composable () -> Unit,
                        supportingContent: @Composable () -> Unit,
                        leadingContent: @Composable () -> Unit,
                        tags: @Composable () -> Unit,
                    ) {
                        val headlineBox = @Composable {
                            ProvideTextStyle(
                                value = MaterialTheme.typography.bodyLarge,
                                content = headlineContent,
                            )
                        }
                        val supportingBox = @Composable {
                            ProvideTextStyle(value = MaterialTheme.typography.bodySmall) {
                                Box(modifier = Modifier.graphicsLayer { alpha = DescriptionAlpha }) {
                                    supportingContent.invoke()
                                }
                            }
                        }
                        val tagsBox = @Composable {
                            ProvideTextStyle(value = MaterialTheme.typography.bodySmall) {
                                Box(modifier = Modifier.graphicsLayer { alpha = SubtitleAlpha }) {
                                    tags.invoke()
                                }
                            }
                        }

                        Layout(
                            modifier = modifier,
                            contents = listOf(headlineBox, supportingBox, leadingContent, tagsBox),
                        ) { (headlineMeasurable, supportingMeasurable, leadingMeasurable, tagsMeasurable), constraints ->
                            val leadingWidth = constraints.maxHeight * 7 / 10
                            val leadingPlaceable = leadingMeasurable.first()
                                .measure(
                                    constraints = constraints.copy(
                                        minWidth = leadingWidth,
                                        maxWidth = leadingWidth,
                                    ),
                                )

                            val leadingPadding = ReleaseItemContainerPaddingHorizontal.roundToPx()
                            val contentWidth = if (constraints.maxWidth == Constraints.Infinity) {
                                constraints.maxWidth
                            } else {
                                (constraints.maxWidth - leadingPlaceable.width - leadingPadding * 2).coerceAtLeast(0)
                            }

                            val tagsPlaceable = tagsMeasurable.first()
                                .measure(
                                    constraints = constraints.copy(
                                        maxWidth = contentWidth,
                                        minHeight = 0,
                                    ),
                                )

                            val spacing = ReleaseItemContentSpacingVertical.roundToPx()
                            val verticalPadding = ReleaseItemContainerPaddingVertical.roundToPx()
                            val doubleVerticalPadding = (ReleaseItemContainerPaddingVertical * 2).roundToPx()
                            val headlineHeight =
                                (constraints.maxHeight - doubleVerticalPadding - tagsPlaceable.height).coerceAtLeast(0)

                            val headlinePlaceable = headlineMeasurable.first()
                                .measure(
                                    constraints = constraints.copy(
                                        maxWidth = contentWidth,
                                        minHeight = 0,
                                        maxHeight = headlineHeight,
                                    ),
                                )

                            val headlineOffset = verticalPadding
                            val tagsOffset = headlineOffset + headlinePlaceable.height

                            val supportingHeight = (headlineHeight - headlinePlaceable.height - spacing).coerceAtLeast(0)
                            val supportingPlaceable = supportingMeasurable.first()
                                .measure(
                                    constraints = constraints.copy(
                                        maxWidth = contentWidth,
                                        minHeight = 0,
                                        maxHeight = supportingHeight,
                                    ),
                                )

                            layout(
                                width = constraints.maxWidth,
                                height = constraints.maxHeight,
                            ) {
                                leadingPlaceable.placeRelative(
                                    x = 0,
                                    y = 0,
                                )
                                headlinePlaceable.placeRelative(
                                    x = leadingWidth + leadingPadding,
                                    y = headlineOffset,
                                )
                                tagsPlaceable.placeRelative(
                                    x = leadingWidth + leadingPadding,
                                    y = tagsOffset,
                                )
                                supportingPlaceable.placeRelative(
                                    x = leadingWidth + leadingPadding,
                                    y = tagsOffset + tagsPlaceable.height + spacing,
                                )
                            }
                        }
                    }
                    
                    @OptIn(ExperimentalMaterial3ExpressiveApi::class)
                    object ExpressiveReleaseListItemDefaults {
                        private var _shape: ExpressiveShape? = null

                        @Composable
                        fun shape(): ExpressiveShape {
                            return _shape ?: RoundedCornerExpressiveShape(
                                shape = RoundedCornerShape(24.dp),
                                pressedShape = RoundedCornerShape(8.dp),
                                selectedShape = RoundedCornerShape(8.dp),
                                animationSpec = spring()
                            ).also { _shape = it }
                        }
                    }

                    private val ReleaseItemContainerPaddingVertical = 12.dp
                    private val ReleaseItemContainerPaddingHorizontal = 16.dp
                    private val ReleaseItemContainerHeight = 160.dp
                    private val ReleaseItemContentSpacingVertical = 4.dp
                    private val ReleaseItemTagsSpacing = 8.dp
                    private const val SubtitleAlpha = 0.6f
                    private const val DescriptionAlpha = 0.8f
                    """.trimIndent()
            )

            //https://medium.com/@donaldokara123/dynamic-pagination-bars-in-compose-multiplatform-c48fe76dafef
            KotlinCode(
                code = """
                    @Composable
                    fun ButtonToken(
                        modifier: Modifier = Modifier,
                        onClick: () -> Unit,
                        buttonType: ComponentType,
                        enabled: Boolean = true,
                        loading: Boolean = false,
                        shape: Shape = MaterialTheme.shapes.medium,
                        elevation: ButtonElevation? = ButtonDefaults.buttonElevation(),
                        border: BorderStroke? = if (buttonType == ComponentType.Outlined) ButtonDefaults.outlinedButtonBorder(enabled) else null,
                        contentPadding: PaddingValues = ButtonDefaults.ContentPadding,
                        interactionSource: MutableInteractionSource? = null,
                        content: @Composable (RowScope.() -> Unit),
                    ) {
                        Button(
                            onClick = onClick,
                            modifier = modifier,
                            enabled = enabled,
                            shape = shape,
                            colors = buttonType.animatedButtonColors(),
                            elevation = elevation,
                            border = border,
                            contentPadding = contentPadding,
                            interactionSource = interactionSource,
                        ) {
                            var contentHeight by remember { mutableStateOf<Dp?>(null) }

                            if (loading) {
                               
                            } else {
                                
                            }
                        }
                    }
                    
                    @Composable
                    fun PaginationBar(
                        pageCount: Int,
                        currentPage: Int,
                        onPageSelected: (Int) -> Unit,
                        modifier: Modifier = Modifier,
                        prevContent: @Composable () -> Unit = { Text("Prev") },
                        nextContent: @Composable () -> Unit = { Text("Next") }
                    ) {
                        BoxWithConstraints(modifier = modifier.fillMaxWidth()) {
                            Layout(
                                content = {
                                    // Always include all buttons and page items
                                    ButtonToken(buttonType = ComponentType.Inverse, onClick = { onPageSelected(currentPage - 1) }, enabled = currentPage > 0) { prevContent() }

                                    repeat(pageCount) { page ->
                                        ButtonToken(
                                       /**
                                        * Use your desired container. See this component below 
                                        * in the director's cut segment
                                        */
                                            buttonType = if (page == currentPage) ComponentType.Inverse else ComponentType.Neutral,
                                            onClick = { onPageSelected(page) }
                                        ) { Text(text = (page + 1).toString()) }
                                    }

                                    ButtonToken(buttonType = ComponentType.Inverse, onClick = { onPageSelected(currentPage + 1) }, enabled = currentPage < pageCount - 1) { nextContent() }
                                },
                                measurePolicy = { measurables, constraints ->
                                    if (measurables.isEmpty()) {
                                        layout(0, 0) {} // empty placement block
                                    } else {
                                        val prevMeasurable = measurables.first()
                                        val nextMeasurable = measurables.last()
                                        val pageMeasurables = measurables.subList(1, measurables.size - 1)

                                        val prevPlaceable = prevMeasurable.measure(constraints)
                                        val nextPlaceable = nextMeasurable.measure(constraints)

                                        val spacingPx = 8.dp.toPx().toInt()
                                        val minItemWidthPx = 40.dp.toPx().toInt()
                                        val availableWidth = constraints.maxWidth - prevPlaceable.width - nextPlaceable.width
                                        val maxVisiblePages = ((availableWidth + spacingPx) / (minItemWidthPx + spacingPx)).coerceAtMost(pageCount).coerceAtLeast(1)

                                        val showPrevNext = maxVisiblePages < pageCount

                                        val startPage = (currentPage - maxVisiblePages / 2).coerceIn(0, pageCount - maxVisiblePages)
                                        val visiblePages = (startPage until startPage + maxVisiblePages)
                                        val pagePlaceables = visiblePages.map { pageMeasurables[it].measure(constraints.copy(minWidth = minItemWidthPx)) }

                                        val totalWidth =
                                            pagePlaceables.sumOf { it.width } +
                                                    (pagePlaceables.size - 1) * spacingPx +
                                                    (if (showPrevNext) prevPlaceable.width + nextPlaceable.width else 0)

                                        layout(totalWidth, maxOf(prevPlaceable.height, nextPlaceable.height, pagePlaceables.maxOf { it.height })) {
                                            var x = 0

                                            if (showPrevNext) {
                                                prevPlaceable.placeRelative(x, 0)
                                                x += prevPlaceable.width + spacingPx
                                            }

                                            pagePlaceables.forEach { placeable ->
                                                placeable.placeRelative(x, 0)
                                                x += placeable.width + spacingPx
                                            }

                                            if (showPrevNext) {
                                                nextPlaceable.placeRelative(x, 0)
                                            }
                                        }
                                    }
                                }
                            )
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    //TextLine using Marquee
                    
                    data class AudioScheduleState(
                        val musics: List<Music> = emptyList(),
                    )

                    data class Music(
                        val type: MysicType,
                        val artist: String,
                        val time: String,
                        val name: String,
                        val description: String,
                    )

                    enum class MysicType(
                        val color: Color,
                    ) {
                        Indie(AudioScheduleColors.orange),
                        Electronic(AudioScheduleColors.lime),
                        Headliner(AudioScheduleColors.purple),
                        Experimental(AudioScheduleColors.pink),
                        AltRock(AudioScheduleColors.blue),
                    }
                    
                    @Composable
                    fun Musictem(
                        item: Music,
                        modifier: Modifier = Modifier,
                    ) {
                        var measurementLayout: TextLayoutResult? by remember { mutableStateOf(null) }
                        var isContentTooLong by remember { mutableStateOf(false) }
                        val timeNameText = "${item.time} • ${item.name}"

                        // Create the full text that would be used in marquee
                        val fullMarqueeText = buildAnnotatedString {
                            withStyle(style = SpanStyle(fontWeight = FontWeight.Normal)) {
                                append(item.artist)
                            }
                            append("                        ") // 24dp spacing
                            withStyle(style = SpanStyle(fontWeight = FontWeight.SemiBold)) {
                                append(timeNameText)
                            }
                        }

                        LaunchedEffect(measurementLayout) {
                            isContentTooLong = (measurementLayout?.lineCount ?: 1) > 1
                        }

                        Column(
                            modifier = modifier
                                .fillMaxWidth()
                                .clip(RoundedCornerShape(12.dp))
                                .background(AudioScheduleColors.surfaceHigher)
                                .semantics {
                                    contentDescription = "${item.artist}, ${item.time}, ${item.name}"
                                    role = Role.Tab
                                }
                                .padding(vertical = 20.dp, horizontal = 16.dp)
                        ) {
                            // Type badge
                            Box(
                                modifier = Modifier
                                    .clip(CircleShape)
                                    .background(item.type.color)
                                    .padding(vertical = 6.dp, horizontal = 10.dp),
                                contentAlignment = Alignment.Center
                            ) {
                                Text(
                                    text = item.type.name.lowercase().replaceFirstChar { it.uppercase() },
                                    fontWeight = FontWeight.Medium,
                                    fontSize = 14.sp,
                                    fontFamily = parkinsansFont,
                                    color = AudioScheduleColors.textPrimary
                                )
                            }

                            Spacer(Modifier.height(20.dp))

                            // Hidden text to measure the full content - this determines our layout
                            Text(
                                text = fullMarqueeText,
                                fontSize = 19.sp,
                                fontFamily = parkinsansFont,
                                onTextLayout = { measurementLayout = it },
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .height(0.dp) // Hidden but still measured
                                    .alpha(0f),
                                maxLines = Int.MAX_VALUE // Allow it to wrap for measurement
                            )

                            if (isContentTooLong) {
                                // Marquee when content is too long
                                Text(
                                    text = fullMarqueeText,
                                    fontSize = 19.sp,
                                    fontFamily = parkinsansFont,
                                    color = AudioScheduleColors.textPrimary,
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .basicMarquee(),
                                    maxLines = 1,
                                    overflow = TextOverflow.Clip
                                )
                            } else {
                                // Side by side when there's space
                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    horizontalArrangement = Arrangement.SpaceBetween
                                ) {
                                    Text(
                                        text = item.artist,
                                        fontWeight = FontWeight.Normal,
                                        fontSize = 19.sp,
                                        fontFamily = parkinsansFont,
                                        color = AudioScheduleColors.textPrimary,
                                        modifier = Modifier.weight(1f, fill = false)
                                    )

                                    Text(
                                        text = timeNameText,
                                        fontWeight = FontWeight.SemiBold,
                                        fontSize = 19.sp,
                                        fontFamily = parkinsansFont,
                                        color = AudioScheduleColors.textPrimary,
                                        textAlign = TextAlign.End
                                    )
                                }
                            }

                            // Description
                            Text(
                                text = item.description,
                                fontWeight = FontWeight.Normal,
                                fontSize = 14.sp,
                                fontFamily = parkinsansFont,
                                color = AudioScheduleColors.textSecondary,
                            )
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/dbarrena/Beauty_CMP.git
            KotlinCode(
                code = $$"""
                    @Composable
                    fun CalendarHourSidebar(
                        hourHeight: Dp,
                        modifier: Modifier = Modifier,
                        label: @Composable (time: LocalTime) -> Unit = { BasicSidebarLabel(time = it) },
                    ) {
                        Column(modifier = modifier) {
                            for (i in 7..20) {
                                val time = LocalTime(hour = i, minute = 0)
                                Box(modifier = Modifier.height(hourHeight)) {
                                    label(time)
                                }
                            }
                        }
                    }

                    @Suppress("NonSkippableComposable")
                    @Composable
                    fun BasicSidebarLabel(
                        time: LocalTime,
                        modifier: Modifier = Modifier,
                    ) {
                        Text(
                            text = time.toHourAmPm(),
                            modifier = modifier
                                .fillMaxHeight()
                                .padding(4.dp)
                        )
                    }

                    private fun LocalTime.toHourAmPm(): String {
                        val hour12 = when {
                            hour == 0 || hour == 12 -> 12
                            hour > 12 -> hour - 12
                            else -> hour
                        }
                        val period = if (hour < 12) "AM" else "PM"
                        return "$hour12 $period"
                    }
                    
                    @Composable
                    fun CalendarScreen(modifier: Modifier = Modifier) {
                        val viewModel = koinViewModel<CalendarScreenViewModel>()

                        CalendarScreenContent(modifier, viewModel)
                    }

                    @Suppress("NonSkippableComposable")
                    @Composable
                    private fun CalendarScreenContent(
                        modifier: Modifier = Modifier,
                        viewModel: CalendarScreenViewModel
                    ) {
                        val state = viewModel.state.collectAsState()
                        val hourHeight = 75.dp
                        val verticalScrollState = rememberScrollState()

                        Row(modifier = modifier) {
                            CalendarHourSidebar(hourHeight, modifier.verticalScroll(verticalScrollState))

                            CalendarEvents(hourHeight, verticalScrollState, state.value.events)
                        }
                    }

                    @Suppress("NonSkippableComposable")
                    @Composable
                    private fun CalendarEvents(hourHeight: Dp, verticalScrollState: ScrollState, events: List<Event>) {
                        val visibleHours = 14 // de 7 a 20 inclusive
                        val hourStart = 7
                        val hourEnd = 20

                        Layout(
                            modifier = Modifier
                                .verticalScroll(verticalScrollState)
                                .drawBehind {
                                    for (i in 1 until visibleHours) {
                                        val y = i * hourHeight.toPx()
                                        drawLine(
                                            Color.LightGray,
                                            start = Offset(0f, y),
                                            end = Offset(size.width, y),
                                            strokeWidth = 1.dp.toPx()
                                        )
                                    }
                                }
                            ,
                            content = {
                                events.sortedBy(Event::start).forEach { event ->
                                    Box(modifier = Modifier.eventData(event)) {
                                        CalendarEventItem(event)
                                    }
                                }
                            }
                        ) { measurables, constraints ->
                            val height = hourHeight.roundToPx() * visibleHours

                            val placeablesWithEvents = measurables.map { measurable ->
                                val event = measurable.parentData as Event
                                val eventHeight = ((minutesBetween(event.start, event.end) / 60f) * hourHeight.toPx()).roundToInt()
                                val placeable = measurable.measure(
                                    constraints.copy(
                                        minHeight = eventHeight,
                                        maxHeight = eventHeight
                                    )
                                )
                                Pair(placeable, event)
                            }

                            layout(constraints.maxWidth, height) {
                                placeablesWithEvents.forEach { (placeable, event) ->
                                    val minutesFromMidnightToEventStart = event.start.hour * 60 + event.start.minute
                                    val minutesOffsetFromStartHour = minutesFromMidnightToEventStart - hourStart * 60

                                    val eventY = ((minutesOffsetFromStartHour / 60f) * hourHeight.toPx()).roundToInt()

                                    placeable.place(0, eventY)
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            //https://bahadireray.medium.com/jetpack-composeda-custom-layout-yazma-her-senaryoya-uyan-ak%C4%B1ll%C4%B1-alertdialog-0e2e258d17ea
            KotlinCode(
                code = """
                    //Custom AlertDialog — Measure & Place
                    
                    @Composable
                    fun CustomAlertLayout(
                        modifier: Modifier = Modifier,
                        title: @Composable (() -> Unit)? = null,
                        description: @Composable (() -> Unit)? = null,
                        icon: @Composable (() -> Unit)? = null,
                        buttons: @Composable (() -> Unit)? = null,
                        content: @Composable (() -> Unit)? = null
                    ) {
                        Layout(
                            modifier = modifier,
                            content = {
                                icon?.invoke()
                                title?.invoke()
                                description?.invoke()
                                content?.invoke()
                                buttons?.invoke()
                            }
                        ) { measurables, constraints ->
                            // 1. MEASURE PHASE
                            val iconPlaceable = if (icon != null) {
                                measurables[0].measure(constraints)
                            } else null
                            
                            val titlePlaceable = if (title != null) {
                                measurables[if (icon != null) 1 else 0].measure(
                                    constraints.copy(
                                        maxWidth = constraints.maxWidth - 48.dp.roundToPx() // Padding
                                    )
                                )
                            } else null
                            
                            val descriptionPlaceable = if (description != null) {
                                val index = when {
                                    icon != null && title != null -> 2
                                    icon != null || title != null -> 1
                                    else -> 0
                                }
                                measurables[index].measure(
                                    constraints.copy(
                                        maxWidth = constraints.maxWidth - 32.dp.roundToPx()
                                    )
                                )
                            } else null
                            
                            val contentPlaceable = if (content != null) {
                                val index = listOfNotNull(icon, title, description).size
                                measurables[index].measure(constraints)
                            } else null
                            
                            val buttonsPlaceable = if (buttons != null) {
                                val index = listOfNotNull(icon, title, description, content).size
                                measurables[index].measure(constraints)
                            } else null
                            
                            // 2. LAYOUT LOGIC
                            val totalHeight = calculateTotalHeight(
                                iconPlaceable,
                                titlePlaceable,
                                descriptionPlaceable,
                                contentPlaceable,
                                buttonsPlaceable
                            )
                            
                            val width = constraints.maxWidth
                            
                            // 3. PLACE PHASE
                            layout(width, totalHeight) {
                                var yPosition = 0
                                
                                iconPlaceable?.let { icon ->
                                    val x = (width - icon.width) / 2
                                    icon.placeRelative(x, yPosition)
                                    yPosition += icon.height + 16.dp.roundToPx()
                                }
                                
                                titlePlaceable?.let { title ->
                                    val x = (width - title.width) / 2
                                    title.placeRelative(x, yPosition)
                                    yPosition += title.height + 8.dp.roundToPx()
                                }
                                
                                descriptionPlaceable?.let { desc ->
                                    val x = (width - desc.width) / 2
                                    desc.placeRelative(x, yPosition)
                                    yPosition += desc.height + 24.dp.roundToPx()
                                }
                                
                                contentPlaceable?.let { content ->
                                    content.placeRelative(0, yPosition)
                                    yPosition += content.height
                                }
                                
                                buttonsPlaceable?.let { buttons ->
                                    buttons.placeRelative(0, yPosition)
                                }
                            }
                        }
                    }

                    private fun calculateTotalHeight(
                        icon: Placeable?,
                        title: Placeable?,
                        description: Placeable?,
                        content: Placeable?,
                        buttons: Placeable?
                    ): Int {
                        var height = 0
                        
                        icon?.let { height += it.height + 16.dp.roundToPx() }
                        title?.let { height += it.height + 8.dp.roundToPx() }
                        description?.let { height += it.height + 24.dp.roundToPx() }
                        content?.let { height += it.height }
                        buttons?.let { height += it.height }
                        
                        // Minimum padding
                        height += 32.dp.roundToPx()
                        
                        return height
                    }
                    """.trimIndent()
            )

            //https://github.com/thementalgoose/android-hour-glass.git
            KotlinCode(
                code = """
                    //ProgressBar With Text Display End of Progress
                    
                    @Composable
                    fun ProgressBar(
                        endProgress: Float,
                        label: (Float) -> String,
                        modifier: Modifier = Modifier,
                        height: Dp = 36.dp,
                        animationDuration: Int = 400,
                        textPadding: Dp = AppTheme.dimensions.paddingSmall,
                        barColor: Color = AppTheme.colors.primary,
                        barOnColor: Color = when (contrastTextLight(barColor.toArgb(), threshold = 140)) {
                            true -> Color.White
                            false -> Color.Black
                        },
                        backgroundColor: Color = AppTheme.colors.backgroundPrimary,
                        backgroundOnColor: Color = AppTheme.colors.textPrimary,
                    ) {

                        BoxWithConstraints(
                            modifier = modifier
                                .fillMaxWidth()
                                .height(height)
                                .clip(RoundedCornerShape(AppTheme.dimensions.radiusSmall))
                        ) {
                            val progressState = remember { mutableFloatStateOf(0f) }
                            val progress = animateFloatAsState(
                                visibilityThreshold = 0.0f,
                                targetValue = progressState.value,
                                animationSpec = tween(
                                    durationMillis = animationDuration,
                                    easing = FastOutSlowInEasing,
                                    delayMillis = 0
                                ), label = "progress_bar"
                            ).value

                            Box(
                                modifier = Modifier
                                    .size(
                                        width = maxWidth,
                                        height = maxHeight
                                    )
                                    .background(backgroundColor)
                            )

                            Box(
                                modifier = Modifier
                                    .size(
                                        width = maxWidth * progress,
                                        height = maxHeight
                                    )
                                    .clip(RoundedCornerShape(AppTheme.dimensions.radiusSmall))
                                    .background(barColor)
                            )

                            MeasureTextWidth(
                                text = label(endProgress),
                                modifier = Modifier
                                    .align(Alignment.CenterStart)
                            ) { textWidth ->

                                val onBar = when {
                                    maxWidth * progress < maxWidth - ((textPadding * 2) + textWidth) -> false
                                    else -> true
                                }

                                Text(
                                    text = label(progress),
                                    style = AppTheme.typography.body1.copy(
                                        color = when (onBar) {
                                            true -> barOnColor
                                            false -> backgroundOnColor
                                        },
                                        fontWeight = FontWeight.Bold
                                    ),
                                    modifier = Modifier
                                        .align(Alignment.CenterStart)
                                        .offset(
                                            x = when (onBar) {
                                                true -> (maxWidth * progress) - (textWidth + textPadding)
                                                false -> (maxWidth * progress) + textPadding
                                            }
                                        )
                                )
                            }

                            LaunchedEffect(endProgress) {
                                progressState.floatValue = endProgress
                            }
                        }
                    }

                    @Composable
                    fun MeasureTextWidth(
                        text: String,
                        modifier: Modifier = Modifier,
                        content: @Composable (width: Dp) -> Unit
                    ) {
                        SubcomposeLayout(
                            modifier = modifier
                        ) { constraints ->
                            val textWidth = subcompose("sampleText") {
                                Text(text)
                            }[0].measure(Constraints()).width.toDp()

                            val contentPlaceable = subcompose("content") {
                                content(textWidth)
                            }[0].measure(constraints)

                            layout(contentPlaceable.width, contentPlaceable.height) {
                                contentPlaceable.place(0, 0)
                            }
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/beytullah-gunduz/container-dashboard.git
            KotlinCode(
                code = """
                    //Three Panel 
                    
                    /**
                     * Navigation state for the three-pane scaffold.
                     */
                    enum class ThreePaneNavigationState {
                        /** Only list and detail panes are visible */
                        ListDetail,

                        /** List, detail, and extra panes are all visible */
                        ListDetailExtra,
                    }

                    /**
                     * Navigator for the ThreePaneScaffold.
                     */
                    class ThreePaneScaffoldNavigator {
                        var currentState by mutableStateOf(ThreePaneNavigationState.ListDetail)
                            private set

                        /**
                         * Shows the extra pane (navigates to ListDetailExtra state).
                         */
                        fun showExtraPane() {
                            currentState = ThreePaneNavigationState.ListDetailExtra
                        }

                        /**
                         * Hides the extra pane (navigates back to ListDetail state).
                         */
                        fun hideExtraPane() {
                            currentState = ThreePaneNavigationState.ListDetail
                        }

                        /**
                         * Checks if the extra pane is currently visible.
                         */
                        val isExtraPaneVisible: Boolean
                            get() = currentState == ThreePaneNavigationState.ListDetailExtra
                    }

                    /**
                     * Creates and remembers a ThreePaneScaffoldNavigator.
                     */
                    @Composable
                    fun rememberThreePaneScaffoldNavigator(): ThreePaneScaffoldNavigator = remember { ThreePaneScaffoldNavigator() }

                    /**
                     * A custom three-pane scaffold for desktop applications with resizable extra pane.
                     *
                     * @param navigator The navigator controlling pane visibility.
                     * @param listPaneWidth The width of the list (sidebar) pane.
                     * @param extraPaneWidthFraction The fraction of available width for the extra pane (0.0 to 1.0).
                     * @param minExtraPaneWidth The minimum width the extra pane can be resized to.
                     * @param listPane The content for the list pane (typically a sidebar).
                     * @param detailPane The content for the detail pane (main content area).
                     * @param extraPane The content for the extra pane (e.g., logs panel).
                     * @param modifier The modifier for the scaffold.
                     */
                    @Composable
                    fun ThreePaneScaffold(
                        navigator: ThreePaneScaffoldNavigator,
                        listPaneWidth: Dp = 220.dp,
                        extraPaneWidthFraction: Float = 0.5f,
                        minExtraPaneWidth: Dp = 300.dp,
                        listPane: @Composable () -> Unit,
                        detailPane: @Composable () -> Unit,
                        extraPane: @Composable () -> Unit,
                        modifier: Modifier = Modifier,
                    ) {
                        BoxWithConstraints(modifier = modifier.fillMaxSize()) {
                            // Calculate initial extra pane width as a fraction of the available content area
                            val availableContentWidth = maxWidth - listPaneWidth
                            val calculatedExtraPaneWidth = (availableContentWidth * extraPaneWidthFraction).coerceAtLeast(minExtraPaneWidth)

                            var extraPaneWidth by remember(calculatedExtraPaneWidth) { mutableStateOf(calculatedExtraPaneWidth) }

                            Row(modifier = Modifier.fillMaxSize()) {
                                // List Pane (Sidebar) - Fixed width
                                Box(modifier = Modifier.width(listPaneWidth).fillMaxHeight()) {
                                    listPane()
                                }

                                // Detail Pane (Main Content) - Takes remaining space
                                Box(modifier = Modifier.weight(1f).fillMaxHeight()) {
                                    detailPane()
                                }

                                // Extra Pane (Logs) with resizable divider - Animated sliding in/out
                                AnimatedVisibility(
                                    visible = navigator.isExtraPaneVisible,
                                    enter =
                                        slideInHorizontally(
                                            initialOffsetX = { it },
                                            animationSpec = tween(durationMillis = 300),
                                        ),
                                    exit =
                                        slideOutHorizontally(
                                            targetOffsetX = { it },
                                            animationSpec = tween(durationMillis = 300),
                                        ),
                                ) {
                                    Row(modifier = Modifier.fillMaxHeight()) {
                                        // Resizable Divider
                                        ResizableDivider(
                                            onDrag = { delta ->
                                                val newWidth = extraPaneWidth - delta
                                                extraPaneWidth = newWidth.coerceAtLeast(minExtraPaneWidth)
                                            },
                                        )

                                        // Extra Pane Content
                                        Box(modifier = Modifier.width(extraPaneWidth).fillMaxHeight()) {
                                            extraPane()
                                        }
                                    }
                                }
                            }
                        }
                    }

                    /**
                     * A vertical divider that can be dragged to resize adjacent panes.
                     */
                    @Composable
                    private fun ResizableDivider(
                        onDrag: (Dp) -> Unit,
                        modifier: Modifier = Modifier,
                    ) {
                        val interactionSource = remember { MutableInteractionSource() }
                        val isHovered by interactionSource.collectIsHoveredAsState()
                        var isDragging by remember { mutableStateOf(false) }
                        val density = LocalDensity.current

                        val dividerColor =
                            when {
                                isDragging -> Color(0xFF0DB7ED) // Docker blue when dragging
                                isHovered -> Color(0xFF0DB7ED).copy(alpha = 0.7f) // Lighter blue on hover
                                else -> Color(0xFF404040) // Dark gray normally
                            }

                        Box(
                            modifier =
                                modifier
                                    .width(6.dp)
                                    .fillMaxHeight()
                                    .hoverable(interactionSource)
                                    .pointerHoverIcon(PointerIcon(Cursor(Cursor.W_RESIZE_CURSOR)))
                                    .pointerInput(Unit) {
                                        detectDragGestures(
                                            onDragStart = { isDragging = true },
                                            onDragEnd = { isDragging = false },
                                            onDragCancel = { isDragging = false },
                                            onDrag = { change, dragAmount ->
                                                change.consume()
                                                with(density) {
                                                    onDrag(dragAmount.x.toDp())
                                                }
                                            },
                                        )
                                    },
                            contentAlignment = Alignment.Center,
                        ) {
                            // Visual divider line
                            Box(
                                modifier =
                                    Modifier
                                        .width(2.dp)
                                        .fillMaxHeight()
                                        .background(dividerColor),
                            )

                            // Drag handle indicator (visible on hover/drag)
                            if (isHovered || isDragging) {
                                Column(
                                    modifier = Modifier.fillMaxHeight(),
                                    verticalArrangement = Arrangement.Center,
                                ) {
                                    // Three dots as resize indicator
                                    repeat(3) {
                                        Box(
                                            modifier =
                                                Modifier
                                                    .padding(vertical = 2.dp)
                                                    .size(4.dp)
                                                    .background(dividerColor, shape = androidx.compose.foundation.shape.CircleShape),
                                        )
                                    }
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/julesmeister/deutschcraft-compose-multiplatform.git
            KotlinCode(
                code = """
                    //Segment Button
                    
                    @Composable
                    fun SegmentedSelector(
                        options: List<String>,
                        selectedIndex: Int,
                        onSelect: (Int) -> Unit,
                        modifier: Modifier = Modifier,
                        accentColor: Color = Indigo
                    ) {
                        val spacing = 2.dp
                        val trackBg = Gray200
                        val indicatorBg = Color.White

                        val density = LocalDensity.current

                        Box(
                            modifier = modifier
                                .fillMaxWidth()
                                .clip(RoundedCornerShape(8.dp))
                                .background(trackBg)
                                .padding(spacing),
                        ) {
                            // Labels row (invisible, just to set the height)
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.spacedBy(spacing),
                            ) {
                                options.forEach {
                                    Box(
                                        modifier = Modifier
                                            .weight(1f)
                                            .padding(vertical = 6.dp),
                                        contentAlignment = Alignment.Center,
                                    ) {
                                        Text(it, style = MaterialTheme.typography.labelMedium, color = Color.Transparent)
                                    }
                                }
                            }

                            // Animated sliding indicator behind text
                            BoxWithConstraints(modifier = Modifier.matchParentSize()) {
                                val containerPx = with(density) { maxWidth.toPx() }
                                val totalSpacingPx = with(density) { (spacing * (options.size - 1)).toPx() }
                                val itemPx = (containerPx - totalSpacingPx) / options.size
                                val spacingPx = with(density) { spacing.toPx() }

                                val animatedOffset by animateFloatAsState(
                                    targetValue = selectedIndex * (itemPx + spacingPx),
                                    animationSpec = spring(
                                        dampingRatio = Spring.DampingRatioLowBouncy,
                                        stiffness = Spring.StiffnessMediumLow,
                                    ),
                                    label = "selector_offset",
                                )

                                Box(
                                    modifier = Modifier
                                        .offset(x = with(density) { animatedOffset.toDp() })
                                        .width(with(density) { itemPx.toDp() })
                                        .fillMaxHeight()
                                        .clip(RoundedCornerShape(6.dp))
                                        .background(indicatorBg),
                                )
                            }

                            // Visible labels on top
                            Row(
                                modifier = Modifier.matchParentSize(),
                                horizontalArrangement = Arrangement.spacedBy(spacing),
                            ) {
                                options.forEachIndexed { index, option ->
                                    val isSelected = index == selectedIndex
                                    Box(
                                        modifier = Modifier
                                            .weight(1f)
                                            .fillMaxHeight()
                                            .clip(RoundedCornerShape(6.dp))
                                            .clickable { onSelect(index) },
                                        contentAlignment = Alignment.Center,
                                    ) {
                                        Text(
                                            text = option,
                                            style = MaterialTheme.typography.labelMedium,
                                            fontWeight = if (isSelected) FontWeight.SemiBold else FontWeight.Medium,
                                            color = if (isSelected) accentColor else Gray500,
                                        )
                                    }
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )
        }
    }
}

//https://github.com/stoyan-vuchev/magic-message.git (Bottom Bar Hide/Show Animation, SharedTransitionLayout, Setting List Good Animation)
//https://github.com/KyNarec/SubSched.git (Desktop Full Screen Option, BoxWithConstraints, Bottom Bar Visible Multiple Navigation)
//https://bahadireray.medium.com (Alert Dialog)
//https://github.com/furkanaskin/ComposePlayground
//https://medium.com/@donaldokara123/dynamic-pagination-bars-in-compose-multiplatform-c48fe76dafef (Dynamic Pagination Bar (- 1 2 3 4 +))
//https://github.com/xephosbot/AnilibriaRefresh
//https://github.com/dbarrena/Beauty_CMP.git
//https://medium.com/@oktaygenc/decoding-the-soul-of-the-compose-runtime-a-deep-anatomy-of-the-recomposer-snapshot-state-and-a79f26862013?postPublishedType=initial
//https://developer.squareup.com/blog/how-jetpack-compose-measuring-works
//https://medium.com/@sivavishnu0705/63f1a832b3af?sk=f8c4e9b2207c4e3f74b563f9ea75e9e1